import React, { useEffect, useState } from "react";
import { fetchProducts } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";

const CAPACITY_OPTIONS = [
  { value: 3, label: "3-Vial Discovery", price: 45 },
  { value: 5, label: "5-Vial Curator", price: 68 },
];

export default function DiscoveryBuilderPage() {
  const [products, setProducts] = useState([]);
  const [capacity, setCapacity] = useState(5);
  const [selected, setSelected] = useState([]); // list of product ids
  const { addToCart, format } = useStore();

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
  }, []);

  const toggleVial = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= capacity) return s;
      return [...s, id];
    });
  };

  const changeCapacity = (cap) => {
    setCapacity(cap);
    setSelected((s) => s.slice(0, cap));
  };

  const colorSlots = Array.from({ length: capacity }).map((_, i) => {
    const id = selected[i];
    const p = products.find((x) => x.id === id);
    return p ? p.accent_color : null;
  });

  const full = selected.length === capacity;
  const packagePrice = CAPACITY_OPTIONS.find((c) => c.value === capacity)?.price || 0;

  const handleAddAll = async () => {
    for (const id of selected) {
      await addToCart(id, "10ml Sample", 1);
    }
  };

  return (
    <main data-testid="discovery-builder-page" className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 lg:py-16">
      <div className="text-center mb-8 sm:mb-10">
        <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C5A059]"><Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> Build Your Own</div>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mt-3">Discovery Set Builder</h1>
        <p className="mt-3 max-w-xl mx-auto text-sm sm:text-base text-[#1A1918]/70">Curate 3 or 5 signature 10ml vials, presented in a velvet-lined box. A perfect gift — or the beginning of your Scentodore wardrobe.</p>
      </div>

      <div className="flex justify-center gap-2 mb-8">
        {CAPACITY_OPTIONS.map((c) => (
          <button
            key={c.value}
            data-testid={`discovery-capacity-${c.value}`}
            onClick={() => changeCapacity(c.value)}
            className={`px-5 py-2.5 rounded-full border text-sm transition-colors ${
              capacity === c.value ? "bg-[#1A1918] text-[#FAF8F5] border-[#1A1918]" : "bg-[#FAF8F5] border-black/10 hover:border-[#C5A059]/40"
            }`}
          >
            {c.label} — {format(c.price)}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-start">
        <div className="lg:sticky lg:top-[100px]">
          <div className="relative rounded-3xl border border-black/10 h-[440px] lg:h-[520px] overflow-hidden bg-gradient-to-br from-[#F1ECE4] to-[#FAF8F5]">
            <div className="absolute inset-0 sd-amber-glow" />
            {/* 2D discovery box preview: elegant ivory tray with vials */}
            <div className="absolute inset-0 flex items-center justify-center p-8">
              <div
                className="relative w-[86%] max-w-[520px] aspect-[16/10] rounded-2xl border border-[#C5A059]/25 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.6)]"
                style={{
                  background:
                    "linear-gradient(135deg, #FAF8F5 0%, #F1ECE4 55%, #E8DFCE 100%)",
                }}
                data-testid="discovery-box-preview"
              >
                {/* Emboss frame */}
                <div className="absolute inset-3 rounded-xl border border-[#C5A059]/20" />
                {/* Vials grid */}
                <div
                  className="absolute inset-0 grid gap-3 p-8 place-items-center"
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(capacity, 5)}, minmax(0, 1fr))`,
                    gridTemplateRows: capacity > 5 ? "repeat(2, 1fr)" : "1fr",
                  }}
                >
                  {Array.from({ length: capacity }).map((_, i) => {
                    const color = colorSlots[i];
                    const filled = Boolean(color);
                    return (
                      <motion.div
                        key={i}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: i * 0.03, duration: 0.4 }}
                        className="relative flex flex-col items-center justify-end"
                      >
                        {/* Vial */}
                        <div
                          className={`relative w-[26px] sm:w-[32px] h-[70px] sm:h-[90px] rounded-b-[6px] rounded-t-[3px] overflow-hidden border ${
                            filled ? "border-[#1A1918]/25" : "border-dashed border-[#1A1918]/15"
                          }`}
                          style={{
                            background: filled
                              ? `linear-gradient(180deg, rgba(255,255,255,0.4) 0%, ${color}CC 45%, ${color} 100%)`
                              : "linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.15) 100%)",
                            boxShadow: filled
                              ? `inset 0 -6px 10px rgba(0,0,0,0.25), 0 6px 12px -6px ${color}80`
                              : "inset 0 -4px 8px rgba(0,0,0,0.05)",
                          }}
                        >
                          {/* Cap */}
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-[70%] h-2 rounded-[3px] bg-[#1A1918]" />
                          {/* Highlight */}
                          <div className="absolute top-1 left-1 w-1 h-8 rounded-full bg-white/45" />
                        </div>
                        <div className="mt-2 text-[9px] uppercase tracking-[0.16em] text-[#1A1918]/50">
                          {(i + 1).toString().padStart(2, "0")}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                {/* Ribbon */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#1A1918] text-[#FAF8F5] text-[9px] uppercase tracking-[0.24em] shadow-md">
                  Scentodore · Discovery
                </div>
              </div>
            </div>
            <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] text-[#1A1918]/60">Slots fill live — {selected.length}/{capacity}</div>
          </div>
          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/60 mb-2">Your Selections</div>
            {selected.length === 0 ? (
              <div className="text-sm text-[#1A1918]/60">No vials chosen yet.</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="discovery-selection-chips">
                <AnimatePresence>
                  {selected.map((id, i) => {
                    const p = products.find((x) => x.id === id);
                    if (!p) return null;
                    return (
                      <motion.button
                        key={id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        onClick={() => toggleVial(id)}
                        className="px-3 py-1.5 rounded-full text-xs border bg-[#C5A059]/15 border-[#C5A059] flex items-center gap-1.5"
                      >
                        {i + 1}. {p.name}
                        <span className="text-[10px] opacity-60">✕</span>
                      </motion.button>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
            <Button
              data-testid="discovery-add-to-cart"
              onClick={handleAddAll}
              disabled={!full}
              className="mt-5 bg-[#C5A059] text-[#1A1918] hover:bg-[#C5A059]/90 rounded-xl px-6 py-5 text-sm uppercase tracking-[0.16em] disabled:opacity-40"
            >
              {full ? `Add Discovery Set to Cart — ${format(packagePrice)}` : `Choose ${capacity - selected.length} more vial${capacity - selected.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="discovery-vial-list">
          {products.map((p) => {
            const chosen = selected.includes(p.id);
            const disabled = !chosen && selected.length >= capacity;
            return (
              <button
                key={p.id}
                data-testid={`discovery-vial-${p.slug}`}
                onClick={() => !disabled && toggleVial(p.id)}
                className={`text-left rounded-2xl border overflow-hidden transition-all ${
                  chosen ? "border-[#C5A059] shadow-[0_10px_30px_rgba(197,160,89,0.18)]" : "border-black/10 hover:border-[#C5A059]/40"
                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <div className="relative aspect-[3/4]" style={{ background: `linear-gradient(160deg, ${p.accent_color}30, #F1ECE4)` }}>
                  <div className="absolute inset-0" style={{ backgroundImage: `url(${p.image_url})`, backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "multiply", opacity: 0.9 }} />
                  {chosen && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#C5A059] text-[#1A1918] flex items-center justify-center">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#1A1918]/60">{p.number}</div>
                  <div className="font-serif text-sm mt-0.5">{p.name}</div>
                  <div className="text-[10px] text-[#1A1918]/60 mt-0.5 truncate">Inspired by {p.inspired_by}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </main>
  );
}
